import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

// Icons
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  LogIn,
  Mail,
} from "lucide-react";

// Components
import { useUser } from "@/hooks/useUser";
import InputError from "../../../components/InputError";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginUser, error, isPending } = useUser();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const user = await loginUser({ ...form });
    const userRoles = user?.jenis_user || [];

    if (!user) return;

    // Routing Logic (Sama seperti sebelumnya)
    if (userRoles.length > 1) navigate("/global/dashboard");
    else if (userRoles.includes("superadmin")) navigate("/admin/dashboard");
    else if (userRoles.includes("pimpinan")) navigate("/pimpinan/dashboard");
    else if (userRoles.includes("siswa")) {
      const tag = searchParams.get("tag");
      navigate(tag ? `/dashboard?tag=${tag}` : "/dashboard");
    } else if (
      userRoles.includes("verifikator_raport") ||
      userRoles.includes("verifikator_raport_all")
    )
      navigate("/verifikator/raport/verifikasi-nilai-raport-smbjm");
    else if (userRoles.includes("verifikator_ukt"))
      navigate("/verifikator/verifikasi-berkas");
    else if (userRoles.some((r) => r.startsWith("loket"))) navigate("/loket");
    else if (userRoles.includes("operator_pasca"))
      navigate("/operator-pasca/dashboard");
    else if (userRoles.includes("verifikator_pasca"))
      navigate("/verifikator-pasca/penilaian");
    else if (userRoles.includes("verifikator_butawarna"))
      navigate("/verifikator/butawarna/dashboard");
    else if (userRoles.includes("verifikator_sehat"))
      navigate("/verifikator/surat-keterangan-sehat/dashboard");
    else if (userRoles.includes("panitia_smbjm"))
      navigate("/panitia-smbjm/ruang-cbt-detail");
    else if (userRoles.includes("akademik"))
      navigate("/akademik/data-pendaftaran");
    else if (userRoles.includes("wawancara_talent"))
      navigate("/wawancara-talentscouting/data-pendaftaran");
    else if (userRoles.includes("operator_internasional"))
      navigate("/operator-internasional/dashboard");
    else if (userRoles.includes("operator_studentexchange"))
      navigate("/operator-studentexchange/data-pendaftaran");
    else navigate("/");
  };

  return (
    <div className="w-full space-y-6">
      <div>
        <Link
          to={
            searchParams.get("tag") === "international"
              ? "/international-admission"
              : searchParams.get("tag") === "student"
                ? "/student-exchange"
                : "/"
          }
          className="inline-flex items-center text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors group"
        >
          <div className="p-1 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30 transition-colors mr-2">
            <ArrowLeft className="w-4 h-4" />
          </div>
          Back
        </Link>
      </div>

      {/* Header Form */}
      <div className="space-y-2 text-center lg:text-left">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
          {t("guest.layout.login.header") || "Welcome Back"}
        </h1>
        <p className="text-slate-500 dark:text-slate-400">
          {t("guest.layout.login.description")}
        </p>
      </div>

      <form onSubmit={handleLogin} className="space-y-5">
        {/* Email Field */}
        <div className="space-y-2">
          <Label
            htmlFor="email"
            className="text-slate-700 dark:text-slate-200 font-semibold"
          >
            Email
          </Label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Mail className="h-4 w-4" />
            </div>
            <Input
              id="email"
              autoComplete="email"
              required
              type="email"
              placeholder="nama@email.com"
              className="pl-10 h-11 bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 focus:bg-white dark:focus:bg-slate-950 transition-all"
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
            />
          </div>
          <InputError text={error?.errors?.email?.join(",")} />
        </div>

        {/* Password Field */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="password"
              className="text-slate-700 dark:text-slate-200 font-semibold"
            >
              Password
            </Label>
            <Link
              to={
                searchParams.get("tag")
                  ? `/forgot-password?tag=${searchParams.get("tag")}`
                  : "/forgot-password"
              }
              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors"
            >
              {t("guest.layout.login.reset-password")}
            </Link>
          </div>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Lock className="h-4 w-4" />
            </div>
            <Input
              id="password"
              autoComplete="current-password"
              required
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              className="pl-10 pr-10 h-11 bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 focus:bg-white dark:focus:bg-slate-950 transition-all"
              value={form.password}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, password: e.target.value }))
              }
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          <InputError text={error?.errors?.password?.join(",")} />
        </div>

        {/* Action Button */}
        <Button
          type="submit"
          className="w-full h-11 text-base font-semibold shadow-md transition-all duration-200"
          disabled={isPending}
        >
          {isPending ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Memproses...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <LogIn className="w-4 h-4" />
              <span>Log in</span>
            </div>
          )}
        </Button>
      </form>

      {/* Footer Links */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-slate-200 dark:border-slate-700" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white lg:bg-white dark:bg-slate-900 px-2 text-slate-500 dark:text-slate-400">
            Don't have an account?
          </span>
        </div>
      </div>

      <div className="text-center">
        <Link
          to={
            searchParams.get("tag")
              ? `/registrasi?tag=${searchParams.get("tag")}`
              : "/registrasi"
          }
          className="inline-flex items-center justify-center w-full h-11 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
        >
          {t("guest.layout.login.new-account") || "Buat Akun Baru"}
        </Link>
      </div>
    </div>
  );
}
